import * as Sentry from '@sentry/react';

import { userActionsTracker } from '@neptune/analytics-domain';

import config from 'config';

import { initTransactions } from './custom-tracing/init';
import { breadcrumbsIntegration } from './breadcrumbs';
import { tracingIntegration, tracingOptions } from './browser-tracing';
import { onlineStatusIntegration } from './online-status-integration';
import { redactEventUrls, SENSITIVE_URL_PARAMS } from './redact-event-urls';

export const isMonitoringEnabled = config.sentryDsn && window.location.hostname !== 'localhost';
export const isUserActionsTrackingEnabled = config.allowUserActionsTracking ?? false;

export function sanitizeUrl(url: string, sensitiveParams = SENSITIVE_URL_PARAMS) {
  const parsedUrl = new URL(url, window.location.origin);

  for (const param of sensitiveParams) {
    if (parsedUrl.searchParams.has(param)) {
      parsedUrl.searchParams.set(param, 'REDACTED');
    }
  }

  return parsedUrl.origin + parsedUrl.pathname + parsedUrl.search;
}

export function initMonitoring() {
  if (isMonitoringEnabled) {
    Sentry.init({
      beforeSend(event) {
        // We want to filter out Unknown series error. It's hard to mute in Sentry because each error has different value.
        // We don't want to filter out errors from error boundary, because those are visible to the user.
        if (!isErrorFromBoundary(event) && isErrorFromUnknownSeries(event)) {
          return null;
        }

        if (typeof event.message === 'string' && event.message.startsWith('Authentication:')) {
          event.fingerprint = [
            event.message,
            navigator.userAgent,
            event.user?.username || 'unknown',
          ];
        }

        if (typeof event.message === 'string' && event.message.startsWith('LoadingIndicator:')) {
          event.fingerprint = ['{{ default }}'];
        }

        return redactEventUrls(event);
      },
      beforeSendTransaction(event) {
        return redactEventUrls(event);
      },
      beforeBreadcrumb(breadcrumb) {
        if (breadcrumb.category === 'navigation' && breadcrumb.data) {
          try {
            breadcrumb.data.from = sanitizeUrl(breadcrumb.data.from);
          } catch {
            breadcrumb.data.from = 'REDACTED';
          }

          try {
            breadcrumb.data.to = sanitizeUrl(breadcrumb.data.to);
          } catch {
            breadcrumb.data.to = 'REDACTED';
          }
        }

        return breadcrumb;
      },
      dsn: config.sentryDsn,
      // for some reason sentry does not accept "/" in release string
      release: config.version.replace(/\//g, '-'),
      ...tracingOptions,
      integrations: [breadcrumbsIntegration, tracingIntegration, onlineStatusIntegration()],
      ignoreErrors: [
        // Random plugins/extensions
        'top.GLOBALS',
        // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
        'originalCreateNotification',
        'canvas.contentDocument',
        'MyApp_RemoveAllHighlights',
        'http://tt.epicplay.com',
        "Can't find variable: ZiteReader",
        'jigsaw is not defined',
        'ComboSearch is not defined',
        'http://loading.retry.widdit.com/',
        'atomicFindClose',
        // Facebook borked
        'fb_xd_fragment',
        // ISP 'optimizing' proxy - `Cache-Control: no-transform` seems to
        // reduce this. (thanks @acdha)
        // See http://stackoverflow.com/questions/4113268
        'bmi_SafeAddOnload',
        'EBCallBackMessageReceived',
        // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
        'conduitPage',
      ],
      denyUrls: [
        // Facebook flakiness
        /graph\.facebook\.com/i,
        // Facebook blocked
        /connect\.facebook\.net\/en_US\/all\.js/i,
        // Woopra flakiness
        /eatdifferent\.com\.woopra-ns\.com/i,
        /static\.woopra\.com\/js\/woopra\.js/i,
        // Browser extensions
        /extensions\//i,
        /^chrome:\/\//i,
        /safari-extension:/i,
        // Other plugins
        /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
        /webappstoolbarba\.texthelp\.com\//i,
        /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
        /\/auryc.lib.js$/i,
      ],
      ...(config.sentryEnvironment ? { environment: config.sentryEnvironment } : {}),
    });

    setTag('component', 'NEPTUNE_FRONTEND');
    setTag('browserId', getOrCreateBrowserId());
    Sentry.setContext('neptune', { browserId: getOrCreateBrowserId() });

    initTransactions();

    document.addEventListener('beforeunload', () => {
      getOrCreateBrowserId();
    });
  }

  if (isUserActionsTrackingEnabled) {
    userActionsTracker.init();
  }
}

let browserId: string;

export function getOrCreateBrowserId() {
  const browserIdKey = 'neptune-bid';
  const stores = {
    localStorage: {
      get: () => localStorage.getItem(browserIdKey),
      set: (val: string) => localStorage.setItem(browserIdKey, val),
    },
    cookie: {
      get: () =>
        document.cookie
          .split('; ')
          .find((row) => row.startsWith(`${browserIdKey}=`))
          ?.split('=')[1],
      set: (val: string) => (document.cookie = `${browserIdKey}=${val}; path=/; max-age=31536000`),
    },
  };

  browserId = stores.localStorage.get() || stores.cookie.get() || browserId || crypto.randomUUID();

  stores.localStorage.set(browserId);
  stores.cookie.set(browserId);

  return browserId;
}

export function setUserContext(userContext: Sentry.User | null) {
  Sentry.configureScope((scope) => {
    scope.setUser(userContext);
  });
}

export function setTag(key: string, value: any) {
  Sentry.configureScope((scope) => {
    scope.setTag(key, value);
  });
}

function isErrorFromUnknownSeries(event: Sentry.Event) {
  return event.exception?.values?.find((exception) => {
    return exception?.value?.startsWith('Unknown series:');
  });
}

function isErrorFromBoundary(event: Sentry.Event) {
  return event.tags?.fromErrorBoundary === true;
}
